/* Shared "choose local files" upload pipeline — the exact flow used by
   /storage/videos, so the dashboard's "Choose files" button uploads the same
   way (auto-split videos vs photos → real upload manager → global UploadPanel),
   instead of the old modal. Self-contained: all deps come from context/redux
   so it can be dropped into any page. */
import { useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { axios as apiAxios, baseUrl } from 'Api';
import { AppContext, AuthContext } from 'Context';
import {
  saveUploadInfo, saveUploadDetails, saveDummy, removeDummy,
} from 'store/store';
import uploadManager from 'Utils/uploadManagerSecure';
import { uploadLargeFile } from 'Utils/uploadWrapper';
import { getBucketName, generateS3Url } from 'Api/awsConfig';
import ForNoImage from 'Assets/Placeholder.png';

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.mkv', '.wmv', '.flv', '.webm', '.m4v', '.3gp', '.mpg', '.mpeg', '.ts', '.mts'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.heic', '.heif', '.tiff', '.tif', '.svg'];
const changeSize = (size) => (size / 1024 / 1024).toFixed(2);
// Thumbnails are captured downscaled to this max width (aspect preserved). A 4K
// frame drawn at source size is a ~33MB canvas and slow to encode; thumbnails
// never need more than this, and capping it keeps multi-file selection smooth.
const THUMBNAIL_MAX_WIDTH = 640;

export const useDirectUpload = () => {
  const {
    setAppSnackbar,
    userDetails,
    setUploadContainer,
    setUploadContainer2,
    setIamUploading,
    setDraftCategoryState,
    base64ToBinary,
    checkTrialUploadLimit,
  } = useContext(AppContext);
  const { getToken } = useContext(AuthContext);
  const userId = userDetails?.isSubscription?.user_id || localStorage.getItem('id');

  const dispatch = useDispatch();
  const uploadPercentages = useSelector((state) => state.upload);
  const photoAlbumIdRef = useRef(null);

  const updateUploadPercentages = (draftId, data) => dispatch(saveUploadInfo({ [draftId]: data }));
  const updateUploadDetails = (draftId, data) => dispatch(saveUploadDetails({ [draftId]: data }));
  const updateDummy = (dummy, status = 0) => {
    if (status === 1) dispatch(saveDummy({ [dummy?.draft_id]: dummy }));
    else dispatch(removeDummy(dummy));
  };

  const getForNoImageBase64 = async () => {
    const response = await fetch(ForNoImage);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const callBackendThumbnailAPI = async (file) => {
    try {
      const videoFilePiece = new FormData();
      videoFilePiece.append('video_file', file.slice(0, 5 * 1024 * 1024));
      const response = await fetch('https://conf.eq7.in/generate-thumbnail/', { method: 'POST', body: videoFilePiece });
      if (!response.ok) throw new Error(`Server error ${response.status}`);
      const data = await response.json();
      if (data?.thumbnail) {
        const res = await fetch(`data:image/png;base64,${data.thumbnail}`);
        const blob = await res.blob();
        return [blob];
      }
      return [];
    } catch (err) {
      console.error('[DirectUpload] Backend thumbnail generation failed:', err);
      return [];
    }
  };

  const generateThumbnailsFromVideo = (file) => new Promise((resolve) => {
    const video = document.createElement('video');
    const videoUrl = URL.createObjectURL(file);
    video.src = videoUrl;
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;

    const fallbackToBackend = async () => {
      URL.revokeObjectURL(videoUrl);
      video.remove();
      const backendBlobs = await callBackendThumbnailAPI(file);
      resolve({ thumbnailBlobs: backendBlobs, videoHeight: 0, videoWidth: 0 });
    };

    video.onerror = () => { fallbackToBackend(); };

    video.onloadeddata = () => {
      const { duration } = video;
      const vHeight = video.videoHeight;
      const vWidth = video.videoWidth;
      const thumbnailCount = 6;
      const interval = duration / (thumbnailCount + 1);
      const tempBlobs = [];
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      let currentIndex = 1;

      const captureNextFrame = () => {
        if (currentIndex > thumbnailCount) {
          URL.revokeObjectURL(videoUrl);
          video.remove();
          if (tempBlobs.length === 0) {
            callBackendThumbnailAPI(file).then((backendBlobs) => {
              resolve({ thumbnailBlobs: backendBlobs, videoHeight: vHeight, videoWidth: vWidth });
            });
            return;
          }
          resolve({ thumbnailBlobs: tempBlobs, videoHeight: vHeight, videoWidth: vWidth });
          return;
        }
        video.currentTime = interval * currentIndex;
        video.onseeked = () => {
          const scale = Math.min(1, THUMBNAIL_MAX_WIDTH / (vWidth || THUMBNAIL_MAX_WIDTH));
          canvas.width = Math.max(1, Math.round(vWidth * scale));
          canvas.height = Math.max(1, Math.round(vHeight * scale));
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
            if (blob) tempBlobs.push(blob);
            currentIndex += 1;
            captureNextFrame();
          }, 'image/jpeg', 0.9);
        };
      };
      captureNextFrame();
    };
  });

  const blobToBase64 = (blob) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });

  const addVideoToAPI = async (draftID, draftState, secureUploadData = null) => {
    const formData = new FormData();
    const draftData = draftState[draftID];
    const isSecureUpload = secureUploadData && secureUploadData.s3Key;
    if (draftData) {
      formData.append('draft_id', draftID || '');
      formData.append('title', draftData.title || '');
      formData.append('category_id', draftData.category || '');
      formData.append('file_name', draftData.fileName || '');
      if (isSecureUpload) {
        formData.append('file_key', secureUploadData.s3Key);
        formData.append('document_id', secureUploadData.documentId || '');
        formData.append('bucket_folder_path', secureUploadData.s3Key);
        if (draftData.thumbnail) formData.append('thumbnail', base64ToBinary(draftData.thumbnail));
        if (secureUploadData.thumbnails && Array.isArray(secureUploadData.thumbnails)) {
          secureUploadData.thumbnails.forEach((thumbnail, index) => {
            if (thumbnail && index !== draftData.selectedThumbnailIndex) {
              const binary = typeof thumbnail === 'string' ? base64ToBinary(thumbnail) : thumbnail;
              formData.append('thumbnails[]', binary);
            }
          });
        }
      } else {
        if (draftData.thumbnail) formData.append('thumbnail', base64ToBinary(draftData.thumbnail));
        if (draftData.thumbnails && Array.isArray(draftData.thumbnails)) {
          draftData.thumbnails.forEach((binary, index) => {
            if (binary && index !== draftData.selectedThumbnailIndex) formData.append('thumbnails[]', binary);
          });
        }
        formData.append('uploadKey', draftData.uploadKey || '');
        formData.append('bucket_folder_path', `${draftData.s3Url}${draftData.uploadBucket}/${draftData.uploadKey}`);
        formData.append('s3_image_bucket_url', draftData.s3Url || '');
      }
      formData.append('description', draftData.description || '');
      formData.append('video_size', changeSize(draftData.videoSize) || '');
      formData.append('group_ids', (draftData.selectedGroups || []).join(','));
      formData.append('visibility', draftData.visibility || '');
      formData.append('subcategory_id', draftData.selectedSubCategory || '');
      formData.append('playlist_ids', draftData.selectedPlaylist || '');
      formData.append('videoHeight', draftData.videoHeight || '');
      formData.append('videoWidth', draftData.videoWidth || '');
      formData.append('videoOreintation', draftData.orientation || '');
      formData.append('s3_status', draftData.uploadStatus || '');
    }
    const response = await apiAxios.post(`${baseUrl}/V6/addVideo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data;
  };

  const startVideoUpload = async (videoFile) => {
    const fileExtension = videoFile.name.split('.').pop().toLowerCase();
    const number = Math.floor(Math.random() * 10000) + 1;
    const fileName = `${Date.now()}-${userId}-video-${number}.${fileExtension}`;
    const bucketName = getBucketName(userId);
    const s3Url = generateS3Url();
    const draftID = uuid();
    const videoSize = videoFile.size;

    const { thumbnailBlobs, videoHeight, videoWidth } = await generateThumbnailsFromVideo(videoFile);
    let thumbnailBase64;
    let thumbnailsBinary;
    if (thumbnailBlobs.length > 0) {
      thumbnailBase64 = await blobToBase64(thumbnailBlobs[0]);
      thumbnailsBinary = await Promise.all(thumbnailBlobs.map((blob) => blobToBase64(blob).then((b64) => base64ToBinary(b64))));
    } else {
      thumbnailBase64 = await getForNoImageBase64();
      thumbnailsBinary = [base64ToBinary(thumbnailBase64)];
    }

    uploadLargeFile({
      userId,
      bucket: bucketName,
      key: fileName,
      file: videoFile,
      draftId: draftID,
      thumbnails: thumbnailsBinary,
      uploadPercentages,
      updateUploadPercentages,
      setUploadContainer,
      setUploadContainer2,
      setIamUploading,
      dummyData: {
        title: videoFile.name.replace(/\.[^/.]+$/, ''),
        fileName: videoFile.name,
        thumbnail: thumbnailBase64,
        thumbnails: thumbnailsBinary,
        ForNoImage,
        category: 'All Videos',
        description: '',
        videoSize,
        selectedGroups: [],
        changeSize,
      },
      updateDummy,
      setDraftCategoryState,
      onSuccess: async (draftId, secureUploadData = null) => {
        const isSecureUpload = secureUploadData && secureUploadData.s3Key;
        const updatedDraftData = {
          title: videoFile.name.replace(/\.[^/.]+$/, ''),
          category: '',
          fileName: videoFile.name,
          thumbnail: thumbnailBase64,
          thumbnails: thumbnailsBinary,
          selectedThumbnailIndex: 0,
          description: '',
          videoSize,
          selectedGroups: [],
          visibility: '2',
          selectedSubCategory: '',
          selectedPlaylist: '',
          videoHeight,
          videoWidth,
          orientation: videoHeight < videoWidth ? 1 : 2,
          uploadStatus: 'completed',
          uploadKey: isSecureUpload ? secureUploadData.s3Key : fileName,
          uploadBucket: isSecureUpload ? '' : bucketName,
          s3Url: isSecureUpload ? '' : s3Url,
        };
        setDraftCategoryState((prevState) => ({ ...prevState, [draftId]: updatedDraftData }));
        try {
          await addVideoToAPI(draftId, { [draftId]: updatedDraftData }, secureUploadData);
        } catch (error) {
          console.error('[DirectUpload] Failed to add video to API:', error);
        }
      },
      onError: (error, draftId) => {
        console.error('[DirectUpload] Upload failed for draft:', draftId, error);
      },
      updateUploadDetails,
    });
  };

  const startPhotoAlbumUpload = async (imageFiles) => {
    let albumId = photoAlbumIdRef.current;
    if (!albumId) {
      try {
        const albumListResponse = await apiAxios.post(`${baseUrl}/getAlbumList`, {});
        const existingAlbums = albumListResponse.data?.data || [];
        const allPhotosAlbum = existingAlbums.find((a) => a.title === 'All Photos');
        if (allPhotosAlbum) albumId = allPhotosAlbum.album_id || allPhotosAlbum.id;
      } catch (err) {
        console.warn('[DirectUpload] Failed to fetch album list, will create new album:', err);
      }
      if (!albumId) {
        const response = await apiAxios.post(`${baseUrl}/addEditAlbum`, {
          title: 'All Photos', description: '', visibility: '2', group_id: '', token: getToken(),
        });
        albumId = response.data?.album_id;
        if (!albumId) { console.error('[DirectUpload] Failed to create album'); return; }
      }
      photoAlbumIdRef.current = albumId;
    }

    const totalPhotos = imageFiles.length;
    const iconBase64 = await blobToBase64(imageFiles[0]);
    uploadManager.createPhotoUpload({
      albumId, albumTitle: 'Photo Album', photoCount: totalPhotos, updateUploadPercentages,
    });
    updateUploadPercentages(albumId, `0/${totalPhotos}`);

    let successfulUploadCount = 0;
    for (let i = 0; i < imageFiles.length; i += 1) {
      try {
        const formData = new FormData();
        formData.append('album_id', albumId);
        formData.append('visibility', '2');
        formData.append('group_id', '');
        if (i === 0 && iconBase64) formData.append('icon', iconBase64);
        formData.append('token', getToken());
        formData.append('photo_file[]', imageFiles[i], imageFiles[i].name);
        // eslint-disable-next-line no-await-in-loop
        await apiAxios.post(`${baseUrl}/addPhotoContent`, formData);
        successfulUploadCount += 1;
        updateUploadPercentages(albumId, `${successfulUploadCount}/${totalPhotos}`);
        uploadManager.updatePhotoUploadProgress(albumId, successfulUploadCount, totalPhotos);
      } catch (error) {
        console.error(`[DirectUpload] Error uploading photo ${i + 1}:`, error);
      }
    }

    updateUploadPercentages(albumId, null);
    uploadManager.completePhotoUpload(albumId);
    setAppSnackbar({ isVisible: true, type: 'success', message: `Uploaded ${successfulUploadCount} ${successfulUploadCount === 1 ? 'photo' : 'photos'}` });
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    const inputElement = event.target;
    inputElement.value = '';
    if (files.length === 0) return;

    const videoFiles = [];
    const imageFiles = [];
    files.forEach((file) => {
      const ext = `.${file.name.split('.').pop().toLowerCase()}`;
      if (VIDEO_EXTENSIONS.includes(ext)) videoFiles.push(file);
      else if (IMAGE_EXTENSIONS.includes(ext)) imageFiles.push(file);
    });

    if (videoFiles.length === 0 && imageFiles.length === 0) {
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Unsupported file type. Please choose videos or photos.' });
      return;
    }

    // Trial allowance: reject the whole batch and show the limit popup BEFORE any
    // upload or thumbnail generation. Thumbnailing decodes every video at full
    // resolution, so bailing here is what keeps an over-limit selection from
    // locking up the browser. No-op for non-trial accounts.
    const mediaFiles = [...videoFiles, ...imageFiles];
    const addBytes = mediaFiles.reduce((sum, f) => sum + (f?.size || 0), 0);
    if (typeof checkTrialUploadLimit === 'function'
      && !checkTrialUploadLimit({ addCount: mediaFiles.length, addBytes })) {
      return;
    }

    videoFiles.forEach((videoFile) => { startVideoUpload(videoFile); });
    if (imageFiles.length > 0) startPhotoAlbumUpload(imageFiles);
  };

  return {
    handleFileSelect, startVideoUpload, startPhotoAlbumUpload, FILE_ACCEPT: `${[...VIDEO_EXTENSIONS, ...IMAGE_EXTENSIONS].join(',')},video/*,image/*`,
  };
};

export default useDirectUpload;
